import React, { ForwardRefRenderFunction, useImperativeHandle, useState } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useOperationDespatchContext } from "../../../../contexts/DespatchContext";
import { Select } from "Components/Form";
import { SearchSelect } from "Components/Form/Input/SearchSelect";
import { saveLabelReport } from "Components/Generic/CartonSSCC/util";
import Modal from "Components/Generic/Modal";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import { isToday } from "date-fns";
import useYupValidation from "Hooks/useYupValidation";
import { SearchNormal1 } from "iconsax-react";
import { Branch } from "Models/branch";
import { useTranslation } from "react-i18next";
import { DispatchReportType, WarehouseEquipmentType } from "Util/enums";
import * as yup from "yup";

import { SubmitButton } from "./components/SubmitButton";
import { ModalContentWrapper, useStyles } from "./styles";
import { useGenerateDispatchReportsActions } from "./useGenerateDispatchReportsActions";
import { getMuiPopupContainer } from "Themes/dispatchTheme";
import { useAppState } from "Context/StateContext";
import { zipPDFReports } from "Util/generateReport";
import SkeletonComponent from "Components/Generic/Skeleton";

export interface IDiaryPrintReportHandle {
  close: () => void;
  open: () => void;
}

interface IDiaryPrintReport {
  disabled: boolean;
  branches?: Branch[];
  isFetchingBranchList?: boolean;
}

const DiaryPrintReport: ForwardRefRenderFunction<IDiaryPrintReportHandle, IDiaryPrintReport> = (
  props,
  ref
) => {
  const { branches, isFetchingBranchList = false } = props;
  const { formatWithUserTimezone, userTimezone, currentUser } = useAppState();

  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { isLoadingCrews, currentDate, viewMode, companyId, branchId } =
    useOperationDespatchContext();
  const { useGenerateReport } = useGenerateDispatchReportsActions();

  const [open, setOpen] = useState<boolean>(false);
  const [reportType, setReportType] = useState<DispatchReportType>(
    DispatchReportType.CREW_SCHEDULE_REPORT
  );
  const [selectedBranchesIdToAdd, setSelectedBranchesIdToAdd] = useState<number[]>([]);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [equipmentType, setEquipmentType] = useState<WarehouseEquipmentType | "">("");

  const handleChangeBranchId = (branchIdAux: string) => {
    const branchId = Number(branchIdAux);

    setSelectedBranchesIdToAdd((prev) => {
      if (prev.includes(branchId)) {
        return prev.filter((id) => id !== branchId);
      }

      return [...prev, branchId];
    });
  };

  const handleSelectAllBranches = () => {
    if (branches) setSelectedBranchesIdToAdd(branches.map((branch) => branch.id!));
  };

  const handleUnselectAllBranches = () => {
    setSelectedBranchesIdToAdd([]);
  };

  const generatedDate = formatWithUserTimezone(currentDate, "EEEE, LLLL dd");

  const { mutate, isLoading } = useGenerateReport((response, variables) => {
    const validResponses = response.filter(Boolean);

    if (!validResponses) {
      NotificationToast.error(translate("noReportGenerated"), {
        id: "no-report-generated",
      });
    } else {
      if (validResponses.length < 11) {
        validResponses.forEach((file) => {
          saveLabelReport(file);
        });
      } else {
        const zipName = `${formatWithUserTimezone(currentDate, "yyyy_MM_dd")}_${
          variables.reportType
        }`;
        zipPDFReports({ pdfReports: validResponses, zipFileName: zipName });
      }

      if (validResponses.length > 0) {
        onCloseModal();
        NotificationToast.success(
          `${validResponses.length} ${
            validResponses.length > 1
              ? translate("reportsGeneratedSuccessfully")
              : translate("reportGeneratedSuccessfully")
          }`,
          {
            id: "report-generated-successfully",
          }
        );
      } else if (isToday(currentDate)) {
        NotificationToast.warning(
          translate("thereAreNoOrderBelongingToTheSelectedBranchesForToday"),
          {
            id: "empty-reports-generated-warning",
          }
        );
      } else {
        NotificationToast.warning(
          `${translate("thereAreNoOrderBelongingToTheSelectedBranchesFor")} ${generatedDate}`,
          {
            id: "empty-reports-generated-warning",
          }
        );
      }
    }
  });

  useImperativeHandle(ref, () => ({ open: onOpenModal, close: onCloseModal }));
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const onOpenModal = () => {
    setOpen(true);
  };

  const onCloseModal = () => {
    setOpen(false);
    setSelectedBranchesIdToAdd([]);
    setErrors([]);
    setReportType(DispatchReportType.CREW_SCHEDULE_REPORT);
    setEquipmentType("");
  };

  const isReportTypeWarehouseSchedule = reportType === DispatchReportType.WAREHOUSE_SCHEDULE_REPORT;
  const isBranchesToAddEmpty = selectedBranchesIdToAdd.length > 0;

  const submitButton = () => {
    if (isReportTypeWarehouseSchedule) {
      return !isBranchesToAddEmpty ? (
        <DefaultTooltip title={translate("youMustSelectAtLeast1BranchToPrint")}>
          <SubmitButton disabled handleSubmit={() => {}} />
        </DefaultTooltip>
      ) : (
        <SubmitButton
          isLoading={isLoading}
          disabled={
            isReportTypeWarehouseSchedule
              ? isLoadingCrews || !isBranchesToAddEmpty || isLoading
              : isLoadingCrews || isLoading
          }
          handleSubmit={() => {
            handleSubmit();
          }}
        />
      );
    }

    return (
      <SubmitButton
        disabled={isLoadingCrews || isLoading}
        isLoading={isLoading}
        handleSubmit={() => {
          handleSubmit();
        }}
      />
    );
  };

  const validationSchema = yup.object().shape({
    reportType: yup
      .mixed()
      .oneOf(Object.values(DispatchReportType))
      .required(translate("reportTypeIsRequired")),
    selectedBranchesIdToAdd: yup.array().when("reportType", {
      is: DispatchReportType.WAREHOUSE_SCHEDULE_REPORT,
      then: yup.array().min(1, translate("atLeastOneBranchMustBeSelected")).required(),
      otherwise: yup.array(),
    }),
  });

  const isValid = () => {
    try {
      validationSchema.validateSync({ reportType, selectedBranchesIdToAdd }, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const handleSubmit = () => {
    if (!isValid()) return;

    const preferences = currentUser!.preferences!;

    switch (reportType) {
      case DispatchReportType.CREW_SCHEDULE_REPORT: {
        const payload = {
          reportType,
          dateSelected: currentDate,
          page: 0,
          pageSize: 100,
          companyId,
          branchId,
          viewMode,
          timezone: userTimezone.name,
          preferences,
        };

        mutate(payload);
        break;
      }

      case DispatchReportType.WAREHOUSE_SCHEDULE_REPORT: {
        const payload = {
          reportType,
          dateSelected: currentDate,
          page: 0,
          pageSize: 100,
          companyId,
          branchId: selectedBranchesIdToAdd,
          viewMode,
          timezone: userTimezone.name,
          preferences,
          ...(equipmentType ? { equipmentType } : {}),
        };

        mutate(payload);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <Modal
      open={open}
      title={translate("printReport")}
      showActionButtons
      actionButtonProps={{
        textCancel: translate("cancel"),
        onCancel: () => {
          onCloseModal();
        },
      }}
      handleClose={onCloseModal}
      showModalFooter
      customRightButtons={submitButton()}
      className={classes.modal}
      container={getMuiPopupContainer}
    >
      <ModalContentWrapper>
        <Select
          required
          disabled={isLoading}
          name="reportType"
          defaultValue=""
          label={translate("selectAReport")}
          value={reportType}
          onChange={(event: SelectChangeEvent<any>) =>
            setReportType(String(event.target.value) as DispatchReportType)
          }
          placeholder={translate("selectAReport")}
          error={fieldHasError("reportType")}
          errorMessage={getErrorByField("reportType")}
        >
          {Object.values(DispatchReportType).map((type) => (
            <MenuItem key={type} value={type}>
              {translate(type)}
            </MenuItem>
          ))}
        </Select>

        {reportType === DispatchReportType.WAREHOUSE_SCHEDULE_REPORT && (
          <SkeletonComponent isLoading={isFetchingBranchList}>
            <>
              <Select
                disabled={isLoading}
                name="equipmentType"
                defaultValue=""
                label={translate("selectEquipmentType")}
                value={equipmentType}
                onChange={(event: SelectChangeEvent<any>) =>
                  setEquipmentType(event.target.value as WarehouseEquipmentType | "")
                }
                placeholder={translate("selectEquipmentType")}
                infoMessage={
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      {translate("infoMessageEquipmentTypeFilterTitle")}
                    </div>

                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      <li>{translate("infoMessageEquipmentTypeFilterNoSelection")}</li>
                      <li>{translate("infoMessageEquipmentTypeFilterSpecificType")}</li>
                      <li>{translate("infoMessageEquipmentTypeFilterNoTasks")}</li>
                    </ul>
                  </div>
                }
              >
                <MenuItem value={WarehouseEquipmentType.FLEET}>{translate("fleet")}</MenuItem>
                <MenuItem value={WarehouseEquipmentType.GENERAL}>{translate("general")}</MenuItem>
              </Select>

              <SearchSelect
                inputProps={{
                  label: translate("selectBranch"),
                  placeholder: translate("quickSearch"),
                  required: true,
                  leftIcon: <SearchNormal1 color={colors.gray500} />,
                  disabled: isLoading,
                }}
                items={
                  branches?.map((branch) => ({
                    value: branch.id ? String(branch.id) : "",
                    label:
                      `${branch.name}${
                        companyId?.length > 1 ? ` - (${branch.company?.name})` : ""
                      }` ?? "",
                    checked: selectedBranchesIdToAdd.includes(branch.id!),
                  })) ?? []
                }
                handleChange={handleChangeBranchId}
                handleSelectAll={handleSelectAllBranches}
                handleUnselectAll={handleUnselectAllBranches}
                checkedCount={selectedBranchesIdToAdd.length}
              />
            </>
          </SkeletonComponent>
        )}
      </ModalContentWrapper>
    </Modal>
  );
};

export default React.forwardRef(DiaryPrintReport);
